// useScrollDepth — fires `scroll_depth` analytics events as the user passes
// 25 / 50 / 75 / 100 % of the document. Each threshold fires at most once per
// mount. Complements useReadGoal (single-threshold "read" goal) by giving a
// full engagement curve on key conversion pages (doc 09 — tracking plan).

import { useEffect, useRef } from "react";
import { track } from "@/lib/analytics";

const DEFAULT_THRESHOLDS = [25, 50, 75, 100] as const;

export function useScrollDepth(
  pageId: string,
  thresholds: readonly number[] = DEFAULT_THRESHOLDS,
) {
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (typeof window === "undefined") return;
    fired.current = new Set();

    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const ratio = (window.scrollY / total) * 100;

      for (const t of thresholds) {
        if (ratio >= t && !fired.current.has(t)) {
          fired.current.add(t);
          track("scroll_depth", { page_id: pageId, depth: t });
        }
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, thresholds.join(",")]);
}
